import {KeyboardEvent, ReactNode, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router";

import {usePrevious} from "@pg-mono/hooks";
import {isEmpty, isEqual} from "@pg-mono/nodash";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {getMainCityBySelectedRegions} from "@pg-rp/interactive-regions-map";

import {IRPStore} from "../../../app/rp_reducer";
import {useClickOutside} from "../../../hooks/handle_click_outside";
import {DEFAULT_DISTANCE, DEFAULT_DISTANCE_FOR_TOWN, SEARCH_DISTANCE_VALUES} from "../../../offer/list/constants/offer_list";
import {useDefaultDistance} from "../../../offer/list/hooks/use_default_distance";
import {Country} from "../../../region/types/Country";
import {resetPlaceList} from "../../actions/fetch_search_places_action";
import {getItemOnClick} from "../../actions/set_item_on_enter";
import {SEARCH_REGIONS_LIMIT} from "../../constants/search_regions";
import {useRegionSearchTabs} from "../../hooks/use_region_search_tabs";
import {hitMapSearch} from "../../tracking/hit_map_search";
import {useTrackAmplitudeGlobalSearch} from "../../tracking/use_track_amplitude_global_search";
import {RegionSearchTabsType} from "../../types/RegionSearchTabsType";
import {SearchTab} from "../../types/SearchTab";
import {addNewRegionToCurrentRegions} from "../../utils/add_new_region_to_current_regions";
import {generatePillLabel, generatePillLabels} from "../../utils/generate_pill_label";
import {getActiveItemLabel} from "../../utils/get_active_item_label";
import {getOffersCountFromSearchValue} from "../../utils/get_offers_count_from_search_value";
import {UseGeolocationWithAlgolytics} from "../../utils/use_geolocation_with_algolytics";
import {ISearchInputValue} from "../ISearchInputValue";
import {
    ISearchAutocompleteActionsProps,
    ISearchAutocompleteOwnProps,
    ISearchAutocompleteStateProps,
    SearchAutocompleteController
} from "./SearchAutocompleteTypes";

interface IProps extends ISearchAutocompleteOwnProps, ISearchAutocompleteStateProps, ISearchAutocompleteActionsProps {}

const searchParams = {regions: true, vendors: true, offers: true, googlePOIs: true};

export const useSearchAutocompleteController = (props: IProps): SearchAutocompleteController => {
    const {isMobile} = useUserDevice();
    const location = useLocation();
    const localDropdownState = useState(false);
    const shouldUseLocalDropdownState = typeof props.isDropdownOpen !== "boolean";
    const isDropdownOpen = shouldUseLocalDropdownState ? localDropdownState[0] : !!props.isDropdownOpen;
    const {activeRegionSearchTab} = useRegionSearchTabs();
    const [isSearchButtonActive, setIsSearchButtonActive] = useState(false);
    const dispatch = useDispatch();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dropdownPortalRef = useRef<HTMLDivElement>(null);
    const latestSearchedLabelRef = useRef<string | null>(null);
    const searchProps = props.getFieldProps("search");
    const searchCountry = useSelector((state: IRPStore) => state.search.currentTabData.country);
    const prevSearchProps = usePrevious(searchProps, searchProps);
    const locationSearchTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);
    const selectedRegions = searchProps.value.tabType === SearchTab.Regions ? searchProps.value.regions : null;

    const mainRegionId = selectedRegions ? getMainCityBySelectedRegions(selectedRegions) : null;
    const mainRegion = mainRegionId ? selectedRegions?.find((region) => region.id === mainRegionId) : null;
    const parentRegion = mainRegionId ? selectedRegions?.find((region) => region?.parent?.id === mainRegionId)?.parent : null;
    const {locationAsked, locateUser} = UseGeolocationWithAlgolytics();
    const {buildAmplitudeSearchQuery, trackAmplitudeSearchAdvanced, setPendingAmplitudeSearch} = useTrackAmplitudeGlobalSearch();

    useEffect(() => {
        const {label} = searchProps.value;
        if (isEmpty(label)) {
            return;
        }

        latestSearchedLabelRef.current = label;
        (async () => {
            const searchTab = await props.fetchAllSearchLists(label, {
                searchContent: searchParams,
                country: searchCountry
            });
            if (searchTab != null) {
                switchTab(searchTab, true);
            }
        })();
    }, []);

    useEffect(() => {
        const {label: prevLabel, ...prevRestProps} = prevSearchProps.value;
        const {label, ...restProps} = searchProps.value;
        if (isEmpty(label) || isEmpty(places)) {
            setIsSearchButtonActive(false);
        }
        if (isEmpty(label) && locationSearchTab === RegionSearchTabsType.TRAVEL_TIME) {
            dispatch(resetPlaceList());
        }
        if (isEmpty(label)) {
            return;
        }
        if (prevLabel === label && latestSearchedLabelRef.current === label) {
            return;
        }
        if (searchProps.value.tabType === SearchTab.Regions && searchProps.value.regions.length >= SEARCH_REGIONS_LIMIT) {
            return;
        }
        if (isEqual(prevRestProps, restProps) || (isEmpty(prevRestProps) && restProps)) {
            (async () => {
                latestSearchedLabelRef.current = label;
                props.clearLabelOfActiveValue();

                const searchTab = await props.fetchAllSearchLists(label, {searchContent: searchParams, country: searchCountry});

                if (searchTab != null) {
                    switchTab(searchTab, true);
                }
            })();
        }
    }, [searchProps.value]);

    useEffect(() => {
        if (isDropdownOpen && props.blockBodyScroll) {
            document.body.classList.add("ReactModal__Body--scroll-block");
        } else {
            document.body.classList.remove("ReactModal__Body--scroll-block");
        }

        if ((isDropdownOpen || props.isInModal) && props.search.currentTabData.currentTab === SearchTab.Places) {
            setIsSearchButtonActive(true);
        }

        if ((isDropdownOpen || props.isInModal) && !locationAsked && location.pathname !== "/") {
            locateUser();
        }
    }, [isDropdownOpen, props.isInModal]);

    useEffect(() => {
        return () => {
            props.stopFetchAllSearchLists();
            props.resetFetchAllSearchLists();
        };
    }, []);

    const handleClickOutside = () => {
        if (locationSearchTab === RegionSearchTabsType.DISTRICTS && isDropdownOpen) {
            const mapRegion = mainRegion || parentRegion;

            hitMapSearch({
                action: "close",
                regionName: mapRegion?.name ?? "na"
            });
        }

        if (!props.isInModal) {
            closeDropdown();
            props.resetActiveDropdownItem();
        }
    };

    useClickOutside(wrapperRef, handleClickOutside, [props.isInModal, isDropdownOpen, locationSearchTab], props.useDropdownPortal ? [dropdownPortalRef] : []);

    const switchTab = (tabType: SearchTab, automatedInitialChange = false) => {
        const searchProps = props.getFieldProps("search");
        props.updateCurrentTab(tabType, searchCountry || Country.POLAND);
        if (props.search.currentTabData.currentTab !== tabType || automatedInitialChange) {
            props.onAfterTabSwitch();
        }
        if (!automatedInitialChange && props.search.currentTabData.currentTab !== tabType && tabType === SearchTab.Places) {
            props.optimizedFetchPlaceList(searchProps.value.label);
        }
    };

    const openDropdown = () => {
        if (shouldUseLocalDropdownState) {
            localDropdownState[1](true);
        }
        props.onDropdownStatusChange(true);
    };

    const closeDropdown = () => {
        if (shouldUseLocalDropdownState) {
            localDropdownState[1](false);
        }
        props.onDropdownStatusChange(false);
    };

    const onKeyDownHandler = async (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Escape") {
            closeDropdown();
            props.resetActiveDropdownItem();
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            openDropdown();
            props.setNextDropdownItem();
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            openDropdown();
            props.setPreviousDropdownItem();
        }
        if (e.key === "Enter") {
            if (isDropdownOpen) {
                const item = await props.setItemOnEnter(props.disableMultiRegion);
                if (item) {
                    if (!props.isTravelTimeTabActive) {
                        setPendingAmplitudeSearch(
                            buildAmplitudeSearchQuery(item as ISearchInputValue),
                            "manual_input",
                            getOffersCountFromSearchValue(item as ISearchInputValue)
                        );
                    }
                    closeDropdown();
                    props.resetActiveDropdownItem();
                    props.onAfterChange?.("search", item as ISearchInputValue);
                }
            } else {
                const currentSearchProps = props.getFieldProps("search");
                if (!props.isTravelTimeTabActive) {
                    setPendingAmplitudeSearch(
                        buildAmplitudeSearchQuery(currentSearchProps.value),
                        "manual_input",
                        getOffersCountFromSearchValue(currentSearchProps.value)
                    );
                }
                closeDropdown();
                props.onSubmit?.();
            }
        }
        setTimeout(() => {
            const searchProps = props.getFieldProps("search");
            props.onAfterChange?.(searchProps.name, searchProps.value);
        }, 0);
    };

    const onLinkClick = async (option: ISearchInputValue, customCloseHandler?: (defaultCloseAction: () => void) => void) => {
        const searchProps = props.getFieldProps("search");
        // non-empty label means user typed a phrase and is selecting from autocomplete (manual_input)
        // SuggestedRegions (popular_search_click) is only shown when label is empty — tracked there
        // travel time tab: selecting an address is not a search initiation, skip global_search
        if (searchProps.value.label && !props.isTravelTimeTabActive) {
            // Build the final combined search value (existing regions + newly selected)
            let trackingValue: ISearchInputValue = option;
            if (option.tabType === SearchTab.Regions && searchProps.value.tabType === SearchTab.Regions) {
                trackingValue = {
                    ...searchProps.value,
                    regions: props.disableMultiRegion ? option.regions : addNewRegionToCurrentRegions(searchProps.value.regions, option.regions?.[0])
                };
            }
            setPendingAmplitudeSearch(buildAmplitudeSearchQuery(trackingValue), "manual_input", getOffersCountFromSearchValue(trackingValue));
        }
        if (props.isTravelTimeTabActive) {
            setIsSearchButtonActive(true);
        } else {
            customCloseHandler ? customCloseHandler(closeDropdown) : closeDropdown();
        }
        if (option.tabType === SearchTab.Regions) {
            let value = {...option, label: ""};
            if (searchProps.value.tabType === SearchTab.Regions) {
                value = {
                    ...searchProps.value,
                    label: "",
                    regions: props.disableMultiRegion ? option.regions : addNewRegionToCurrentRegions(searchProps.value.regions, option.regions[0])
                };
            }
            props.onChange({search: value});
            setTimeout(() => props.onAfterChange?.(searchProps.name, value), 0);
            return;
        }
        const createdOption = (await getItemOnClick(option)) as ISearchInputValue;
        props.onChange({search: createdOption});
        props.resetActiveDropdownItem();
        if (props.isTravelTimeTabActive) {
            return;
        }
        if (createdOption.tabType === SearchTab.Places) {
            closeDropdown();
        }
        if (!props.isTravelTimeTabActive) {
            setTimeout(() => props.onAfterChange?.(searchProps.name, option), 0);
        }
    };

    const updateSelectedRegions = async (option: ISearchInputValue) => {
        if (option.tabType !== SearchTab.Regions) {
            return;
        }

        const searchProps = props.getFieldProps("search");
        let value = {...option, label: ""};
        if (searchProps.value.tabType === SearchTab.Regions) {
            value = {
                ...searchProps.value,
                label: "",
                regions: option.regions
            };
        }

        props.onChange({search: value});
        closeDropdown();
        trackAmplitudeSearchAdvanced({searchOverride: value});
        setTimeout(() => props.onAfterChange?.(searchProps.name, value), 0);
    };

    const onRemoveRegionTagClick = (regionId: number | null) => {
        const searchProps = props.getFieldProps("search");
        if (searchProps.value.tabType === SearchTab.Regions) {
            const filteredRegions = regionId == null ? [] : searchProps.value.regions.filter((region) => region.id !== regionId);
            const value = {...searchProps.value, regions: filteredRegions};
            props.onChange({search: value});
            props.onAfterChange && props.onAfterChange(searchProps.name, value);
            return closeDropdown();
        }
    };

    const onRemoveAllRegions = () => {
        const searchProps = props.getFieldProps("search");
        if (searchProps.value.tabType === SearchTab.Regions) {
            const value = {...searchProps.value, regions: []};
            props.onChange({search: value});
            props.onAfterChange && props.onAfterChange(searchProps.name, value);
            return closeDropdown();
        }
    };

    const {fetchRegionsRequest, fetchPlacesRequest, fetchOffersRequest, fetchVendorsRequest} = props.search;
    const requestStateObj = {fetchRegionsRequest, fetchPlacesRequest, fetchOffersRequest, fetchVendorsRequest};
    const {offers, vendors, regions, places} = props.search;
    const pillLabelOptions = {hiddenRegionsLabelVariant: isMobile ? ("compact" as const) : ("full" as const)};
    const regionPillLabel = generatePillLabel(isDropdownOpen, searchProps.value, pillLabelOptions);
    const regionPillLabels = generatePillLabels(searchProps.value, pillLabelOptions);
    const isAbroadOrHolidayTab = [SearchTab.Holiday].some((tab) => tab === props.search.currentTabData.currentTab) || searchCountry === Country.SPAIN;
    const defaultPlaceholder = (props.placeholder ?? isAbroadOrHolidayTab) ? "Wybierz lokalizację" : "Gdzie chcesz mieszkać?";
    const defaultAddRegionPlaceholder = props.disableMultiRegion ? "Zmień lokalizację" : "Dodaj lokalizację";
    const addRegionPlaceholder = props.addedRegionPlaceholder?.(!props.disableMultiRegion) ?? defaultAddRegionPlaceholder;
    const isRegionSelected = searchProps.value.tabType === SearchTab.Regions && searchProps.value.regions.length >= 1;
    const placeholder = isRegionSelected ? addRegionPlaceholder : defaultPlaceholder;
    const activeItemLabel = getActiveItemLabel(props.search.activeDropdownItem.id, props.search);
    const defaultDistanceValue = useDefaultDistance(isAbroadOrHolidayTab ? DEFAULT_DISTANCE_FOR_TOWN : props.values?.distance);
    const pillDistanceValue = typeof props.values?.distance === "number" ? props.values.distance : 5;
    const prevDistance =
        typeof props.search.travelTime.prevDistance === "number"
            ? props.search.travelTime.prevDistance
            : props.search.travelTime.prevDistance &&
                !isNaN(parseInt(props.search.travelTime.prevDistance)) &&
                SEARCH_DISTANCE_VALUES.includes(parseInt(props.search.travelTime.prevDistance))
              ? props.search.travelTime.prevDistance
              : DEFAULT_DISTANCE;

    const onTabTypeClick = (newTabType: SearchTab, country?: Country) => {
        props.updateCurrentTab(newTabType, country);
        const searchProps = props.getFieldProps("search");
        searchProps.onChange(searchProps.name, {...searchProps.value, label: ""});
        props.resetFetchAllSearchLists();
    };

    const dropdownListProps = {
        dropdownIsOpen: isDropdownOpen,
        search: searchProps.value,
        offers,
        requestStateObj,
        vendors,
        activeItem: props.search.activeDropdownItem,
        places,
        regions,
        selectedTab: props.search.currentTabData.currentTab,
        onLinkClick,
        updateSelectedRegions,
        removeRegionTag: onRemoveRegionTagClick,
        onClose: () => {
            closeDropdown();
            setIsSearchButtonActive(false);
            props.resetActiveDropdownItem();
        },
        getFieldProps: props.getFieldProps,
        setTravelTimeData: props.setTravelTimeData,
        prevDistance,
        isSearchButtonActive,
        hideRecentFilters: props.hideRecentFilters
    };

    return {
        wrapperRef,
        dropdownPortalRef,
        disableRelativeWrapperPadding: Boolean(props.showSelectedUnderInput || props.disableExtraMargins),
        onKeyDownHandler,
        isDropdownOpen,
        openDropdown,
        closeDropdown,
        inputProps: {
            ...searchProps,
            autoFocus: props.autoFocus,
            openDropdown,
            closeDropdown,
            activeItemLabel,
            placeholder,
            regionPillLabel,
            regionPillLabels,
            isDropdownOpen,
            inputIcon: props.inputIcon,
            inputWrapperCss: props.inputWrapperCss,
            inputContainerCss: props.inputContainerCss,
            iconWrapperCss: props.iconWrapperCss,
            inputCss: props.inputCss,
            regionPillWrapperCss: props.regionPillWrapperCss,
            hideArrowIcon: props.hideArrowIcon,
            onRemoveAllRegions,
            onInputMount: props.onInputMount,
            isInputWithDistanceFilter: props.includeDistanceFilter,
            showArrowOnMobile: props.showArrowOnMobile,
            onInputClick: props.onInputClick,
            isTravelTimeTabActive: props.isTravelTimeTabActive,
            inputCoreWrapperCss: props.inputCoreWrapperCss,
            dropdownIcon: props.dropdownIcon
        },
        searchProps,
        onTabTypeClick,
        search: props.search,
        searchCountry,
        activeRegionSearchTab,
        isAbroadOrHolidayTab,
        isRegionSelected,
        shouldRenderDistanceFilter: !!props.includeDistanceFilter,
        distanceFilterProps: {
            defaultValue: defaultDistanceValue,
            values: props.values ?? ({search: {label: "", tabType: null}} as SearchAutocompleteController["distanceFilterProps"]["values"]),
            onAfterChange: props.onAfterChange,
            onChange: props.onChange,
            handleDistanceSubmit: () => null,
            handleDistanceAfterChange: () => undefined,
            setDesktopTypeBlendActive: () => null,
            onFilterOpen: props.isInModal ? undefined : closeDropdown,
            isMobileDropdown: true
        },
        searchBarContainerClassName: props.searchBarContainerClassName,
        disableSearchbarExtraMargins: Boolean(props.showSelectedUnderInput || props.disableExtraMargins),
        dropdownProps: {
            isOpen: isDropdownOpen,
            isInModal: props.isInModal,
            "data-testid": NAVIGATION_TEST_IDS.SEARCH_BAR.DROPDOWN,
            isInputWithDistanceFilter: props.search.currentTabData.country === Country.POLAND && !!props.includeDistanceFilter,
            isHomepage: props.isHomepage,
            activeRegionSearchTab,
            className: props.dropdownWrapperClassName,
            minWidth: props.minWidth
        },
        dropdownListProps,
        renderDropdownList: () => props.renderDropdownList(dropdownListProps) as ReactNode,
        requestStateObj,
        selectedRegions,
        isMobile: Boolean(isMobile),
        pillDistanceValue,
        prevDistance,
        onRemoveRegionTagClick,
        showSelectedUnderInput: Boolean(
            props.showSelectedUnderInput &&
            props.values?.search &&
            "regions" in props.values.search &&
            props.values.search?.regions?.length &&
            !props.isTravelTimeTabActive
        ),
        offers,
        vendors,
        regions,
        places,
        activeItem: props.search.activeDropdownItem
    };
};
import {KeyboardEvent, ReactNode, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router";

import {usePrevious} from "@pg-mono/hooks";
import {isEmpty, isEqual} from "@pg-mono/nodash";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {getMainCityBySelectedRegions} from "@pg-rp/interactive-regions-map";

import {IRPStore} from "../../../app/rp_reducer";
import {useClickOutside} from "../../../hooks/handle_click_outside";
import {DEFAULT_DISTANCE, DEFAULT_DISTANCE_FOR_TOWN, SEARCH_DISTANCE_VALUES} from "../../../offer/list/constants/offer_list";
import {useDefaultDistance} from "../../../offer/list/hooks/use_default_distance";
import {Country} from "../../../region/types/Country";
import {resetPlaceList} from "../../actions/fetch_search_places_action";
import {getItemOnClick} from "../../actions/set_item_on_enter";
import {SEARCH_REGIONS_LIMIT} from "../../constants/search_regions";
import {useRegionSearchTabs} from "../../hooks/use_region_search_tabs";
import {hitMapSearch} from "../../tracking/hit_map_search";
import {useTrackAmplitudeGlobalSearch} from "../../tracking/use_track_amplitude_global_search";
import {RegionSearchTabsType} from "../../types/RegionSearchTabsType";
import {SearchTab} from "../../types/SearchTab";
import {addNewRegionToCurrentRegions} from "../../utils/add_new_region_to_current_regions";
import {generatePillLabel, generatePillLabels} from "../../utils/generate_pill_label";
import {getActiveItemLabel} from "../../utils/get_active_item_label";
import {getOffersCountFromSearchValue} from "../../utils/get_offers_count_from_search_value";
import {UseGeolocationWithAlgolytics} from "../../utils/use_geolocation_with_algolytics";
import {ISearchInputValue} from "../ISearchInputValue";
import {
    ISearchAutocompleteActionsProps,
    ISearchAutocompleteOwnProps,
    ISearchAutocompleteStateProps,
    SearchAutocompleteController
} from "./SearchAutocompleteTypes";

interface IProps extends ISearchAutocompleteOwnProps, ISearchAutocompleteStateProps, ISearchAutocompleteActionsProps {}

const searchParams = {regions: true, vendors: true, offers: true, googlePOIs: true};

export const useSearchAutocompleteController = (props: IProps): SearchAutocompleteController => {
    const {isMobile} = useUserDevice();
    const location = useLocation();
    const localDropdownState = useState(false);
    const shouldUseLocalDropdownState = typeof props.isDropdownOpen !== "boolean";
    const isDropdownOpen = shouldUseLocalDropdownState ? localDropdownState[0] : !!props.isDropdownOpen;
    const {activeRegionSearchTab} = useRegionSearchTabs();
    const [isSearchButtonActive, setIsSearchButtonActive] = useState(false);
    const dispatch = useDispatch();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dropdownPortalRef = useRef<HTMLDivElement>(null);
    const latestSearchedLabelRef = useRef<string | null>(null);
    const searchProps = props.getFieldProps("search");
    const searchCountry = useSelector((state: IRPStore) => state.search.currentTabData.country);
    const prevSearchProps = usePrevious(searchProps, searchProps);
    const locationSearchTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);
    const selectedRegions = searchProps.value.tabType === SearchTab.Regions ? searchProps.value.regions : null;

    const mainRegionId = selectedRegions ? getMainCityBySelectedRegions(selectedRegions) : null;
    const mainRegion = mainRegionId ? selectedRegions?.find((region) => region.id === mainRegionId) : null;
    const parentRegion = mainRegionId ? selectedRegions?.find((region) => region?.parent?.id === mainRegionId)?.parent : null;
    const {locationAsked, locateUser} = UseGeolocationWithAlgolytics();
    const {buildAmplitudeSearchQuery, trackAmplitudeSearchAdvanced, setPendingAmplitudeSearch} = useTrackAmplitudeGlobalSearch();

    useEffect(() => {
        const {label} = searchProps.value;
        if (isEmpty(label)) {
            return;
        }

        latestSearchedLabelRef.current = label;
        (async () => {
            const searchTab = await props.fetchAllSearchLists(label, {
                searchContent: searchParams,
                country: searchCountry
            });
            if (searchTab != null) {
                switchTab(searchTab, true);
            }
        })();
    }, []);

    useEffect(() => {
        const {label: prevLabel, ...prevRestProps} = prevSearchProps.value;
        const {label, ...restProps} = searchProps.value;
        if (isEmpty(label) || isEmpty(places)) {
            setIsSearchButtonActive(false);
        }
        if (isEmpty(label) && locationSearchTab === RegionSearchTabsType.TRAVEL_TIME) {
            dispatch(resetPlaceList());
        }
        if (isEmpty(label)) {
            return;
        }
        if (prevLabel === label && latestSearchedLabelRef.current === label) {
            return;
        }
        if (searchProps.value.tabType === SearchTab.Regions && searchProps.value.regions.length >= SEARCH_REGIONS_LIMIT) {
            return;
        }
        if (isEqual(prevRestProps, restProps) || (isEmpty(prevRestProps) && restProps)) {
            (async () => {
                latestSearchedLabelRef.current = label;
                props.clearLabelOfActiveValue();

                const searchTab = await props.fetchAllSearchLists(label, {searchContent: searchParams, country: searchCountry});

                if (searchTab != null) {
                    switchTab(searchTab, true);
                }
            })();
        }
    }, [searchProps.value]);

    useEffect(() => {
        if (isDropdownOpen && props.blockBodyScroll) {
            document.body.classList.add("ReactModal__Body--scroll-block");
        } else {
            document.body.classList.remove("ReactModal__Body--scroll-block");
        }

        if ((isDropdownOpen || props.isInModal) && props.search.currentTabData.currentTab === SearchTab.Places) {
            setIsSearchButtonActive(true);
        }

        if ((isDropdownOpen || props.isInModal) && !locationAsked && location.pathname !== "/") {
            locateUser();
        }
    }, [isDropdownOpen, props.isInModal]);

    useEffect(() => {
        return () => {
            props.stopFetchAllSearchLists();
            props.resetFetchAllSearchLists();
        };
    }, []);

    const handleClickOutside = () => {
        if (locationSearchTab === RegionSearchTabsType.DISTRICTS && isDropdownOpen) {
            const mapRegion = mainRegion || parentRegion;

            hitMapSearch({
                action: "close",
                regionName: mapRegion?.name ?? "na"
            });
        }

        if (!props.isInModal) {
            closeDropdown();
            props.resetActiveDropdownItem();
        }
    };

    useClickOutside(wrapperRef, handleClickOutside, [props.isInModal, isDropdownOpen, locationSearchTab], props.useDropdownPortal ? [dropdownPortalRef] : []);

    const switchTab = (tabType: SearchTab, automatedInitialChange = false) => {
        const searchProps = props.getFieldProps("search");
        props.updateCurrentTab(tabType, searchCountry || Country.POLAND);
        if (props.search.currentTabData.currentTab !== tabType || automatedInitialChange) {
            props.onAfterTabSwitch();
        }
        if (!automatedInitialChange && props.search.currentTabData.currentTab !== tabType && tabType === SearchTab.Places) {
            props.optimizedFetchPlaceList(searchProps.value.label);
        }
    };

    const openDropdown = () => {
        if (shouldUseLocalDropdownState) {
            localDropdownState[1](true);
        }
        props.onDropdownStatusChange(true);
    };

    const closeDropdown = () => {
        if (shouldUseLocalDropdownState) {
            localDropdownState[1](false);
        }
        props.onDropdownStatusChange(false);
    };

    const onKeyDownHandler = async (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Escape") {
            closeDropdown();
            props.resetActiveDropdownItem();
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            openDropdown();
            props.setNextDropdownItem();
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            openDropdown();
            props.setPreviousDropdownItem();
        }
        if (e.key === "Enter") {
            if (isDropdownOpen) {
                const item = await props.setItemOnEnter(props.disableMultiRegion);
                if (item) {
                    if (!props.isTravelTimeTabActive) {
                        setPendingAmplitudeSearch(
                            buildAmplitudeSearchQuery(item as ISearchInputValue),
                            "manual_input",
                            getOffersCountFromSearchValue(item as ISearchInputValue)
                        );
                    }
                    closeDropdown();
                    props.resetActiveDropdownItem();
                    props.onAfterChange?.("search", item as ISearchInputValue);
                }
            } else {
                const currentSearchProps = props.getFieldProps("search");
                if (!props.isTravelTimeTabActive) {
                    setPendingAmplitudeSearch(
                        buildAmplitudeSearchQuery(currentSearchProps.value),
                        "manual_input",
                        getOffersCountFromSearchValue(currentSearchProps.value)
                    );
                }
                closeDropdown();
                props.onSubmit?.();
            }
        }
        setTimeout(() => {
            const searchProps = props.getFieldProps("search");
            props.onAfterChange?.(searchProps.name, searchProps.value);
        }, 0);
    };

    const onLinkClick = async (option: ISearchInputValue, customCloseHandler?: (defaultCloseAction: () => void) => void) => {
        const searchProps = props.getFieldProps("search");
        // non-empty label means user typed a phrase and is selecting from autocomplete (manual_input)
        // SuggestedRegions (popular_search_click) is only shown when label is empty — tracked there
        // travel time tab: selecting an address is not a search initiation, skip global_search
        if (searchProps.value.label && !props.isTravelTimeTabActive) {
            // Build the final combined search value (existing regions + newly selected)
            let trackingValue: ISearchInputValue = option;
            if (option.tabType === SearchTab.Regions && searchProps.value.tabType === SearchTab.Regions) {
                trackingValue = {
                    ...searchProps.value,
                    regions: props.disableMultiRegion ? option.regions : addNewRegionToCurrentRegions(searchProps.value.regions, option.regions?.[0])
                };
            }
            setPendingAmplitudeSearch(buildAmplitudeSearchQuery(trackingValue), "manual_input", getOffersCountFromSearchValue(trackingValue));
        }
        if (props.isTravelTimeTabActive) {
            setIsSearchButtonActive(true);
        } else {
            customCloseHandler ? customCloseHandler(closeDropdown) : closeDropdown();
        }
        if (option.tabType === SearchTab.Regions) {
            let value = {...option, label: ""};
            if (searchProps.value.tabType === SearchTab.Regions) {
                value = {
                    ...searchProps.value,
                    label: "",
                    regions: props.disableMultiRegion ? option.regions : addNewRegionToCurrentRegions(searchProps.value.regions, option.regions[0])
                };
            }
            props.onChange({search: value});
            setTimeout(() => props.onAfterChange?.(searchProps.name, value), 0);
            return;
        }
        const createdOption = (await getItemOnClick(option)) as ISearchInputValue;
        props.onChange({search: createdOption});
        props.resetActiveDropdownItem();
        if (props.isTravelTimeTabActive) {
            return;
        }
        if (createdOption.tabType === SearchTab.Places) {
            closeDropdown();
        }
        if (!props.isTravelTimeTabActive) {
            setTimeout(() => props.onAfterChange?.(searchProps.name, option), 0);
        }
    };

    const updateSelectedRegions = async (option: ISearchInputValue) => {
        if (option.tabType !== SearchTab.Regions) {
            return;
        }

        const searchProps = props.getFieldProps("search");
        let value = {...option, label: ""};
        if (searchProps.value.tabType === SearchTab.Regions) {
            value = {
                ...searchProps.value,
                label: "",
                regions: option.regions
            };
        }

        props.onChange({search: value});
        closeDropdown();
        trackAmplitudeSearchAdvanced({searchOverride: value});
        setTimeout(() => props.onAfterChange?.(searchProps.name, value), 0);
    };

    const onRemoveRegionTagClick = (regionId: number | null) => {
        const searchProps = props.getFieldProps("search");
        if (searchProps.value.tabType === SearchTab.Regions) {
            const filteredRegions = regionId == null ? [] : searchProps.value.regions.filter((region) => region.id !== regionId);
            const value = {...searchProps.value, regions: filteredRegions};
            props.onChange({search: value});
            props.onAfterChange && props.onAfterChange(searchProps.name, value);
            return closeDropdown();
        }
    };

    const onRemoveAllRegions = () => {
        const searchProps = props.getFieldProps("search");
        if (searchProps.value.tabType === SearchTab.Regions) {
            const value = {...searchProps.value, regions: []};
            props.onChange({search: value});
            props.onAfterChange && props.onAfterChange(searchProps.name, value);
            return closeDropdown();
        }
    };

    const {fetchRegionsRequest, fetchPlacesRequest, fetchOffersRequest, fetchVendorsRequest} = props.search;
    const requestStateObj = {fetchRegionsRequest, fetchPlacesRequest, fetchOffersRequest, fetchVendorsRequest};
    const {offers, vendors, regions, places} = props.search;
    const pillLabelOptions = {hiddenRegionsLabelVariant: isMobile ? ("compact" as const) : ("full" as const)};
    const regionPillLabel = generatePillLabel(isDropdownOpen, searchProps.value, pillLabelOptions);
    const regionPillLabels = generatePillLabels(searchProps.value, pillLabelOptions);
    const isAbroadOrHolidayTab = [SearchTab.Holiday].some((tab) => tab === props.search.currentTabData.currentTab) || searchCountry === Country.SPAIN;
    const defaultPlaceholder = (props.placeholder ?? isAbroadOrHolidayTab) ? "Wybierz lokalizację" : "Gdzie chcesz mieszkać?";
    const defaultAddRegionPlaceholder = props.disableMultiRegion ? "Zmień lokalizację" : "Dodaj lokalizację";
    const addRegionPlaceholder = props.addedRegionPlaceholder?.(!props.disableMultiRegion) ?? defaultAddRegionPlaceholder;
    const isRegionSelected = searchProps.value.tabType === SearchTab.Regions && searchProps.value.regions.length >= 1;
    const placeholder = isRegionSelected ? addRegionPlaceholder : defaultPlaceholder;
    const activeItemLabel = getActiveItemLabel(props.search.activeDropdownItem.id, props.search);
    const defaultDistanceValue = useDefaultDistance(isAbroadOrHolidayTab ? DEFAULT_DISTANCE_FOR_TOWN : props.values?.distance);
    const pillDistanceValue = typeof props.values?.distance === "number" ? props.values.distance : 5;
    const prevDistance =
        typeof props.search.travelTime.prevDistance === "number"
            ? props.search.travelTime.prevDistance
            : props.search.travelTime.prevDistance &&
                !isNaN(parseInt(props.search.travelTime.prevDistance)) &&
                SEARCH_DISTANCE_VALUES.includes(parseInt(props.search.travelTime.prevDistance))
              ? props.search.travelTime.prevDistance
              : DEFAULT_DISTANCE;

    const onTabTypeClick = (newTabType: SearchTab, country?: Country) => {
        props.updateCurrentTab(newTabType, country);
        const searchProps = props.getFieldProps("search");
        searchProps.onChange(searchProps.name, {...searchProps.value, label: ""});
        props.resetFetchAllSearchLists();
    };

    const dropdownListProps = {
        dropdownIsOpen: isDropdownOpen,
        search: searchProps.value,
        offers,
        requestStateObj,
        vendors,
        activeItem: props.search.activeDropdownItem,
        places,
        regions,
        selectedTab: props.search.currentTabData.currentTab,
        onLinkClick,
        updateSelectedRegions,
        removeRegionTag: onRemoveRegionTagClick,
        onClose: () => {
            closeDropdown();
            setIsSearchButtonActive(false);
            props.resetActiveDropdownItem();
        },
        getFieldProps: props.getFieldProps,
        setTravelTimeData: props.setTravelTimeData,
        prevDistance,
        isSearchButtonActive,
        hideRecentFilters: props.hideRecentFilters
    };

    return {
        wrapperRef,
        dropdownPortalRef,
        disableRelativeWrapperPadding: Boolean(props.showSelectedUnderInput || props.disableExtraMargins),
        onKeyDownHandler,
        isDropdownOpen,
        openDropdown,
        closeDropdown,
        inputProps: {
            ...searchProps,
            autoFocus: props.autoFocus,
            openDropdown,
            closeDropdown,
            activeItemLabel,
            placeholder,
            regionPillLabel,
            regionPillLabels,
            isDropdownOpen,
            inputIcon: props.inputIcon,
            inputWrapperCss: props.inputWrapperCss,
            inputContainerCss: props.inputContainerCss,
            iconWrapperCss: props.iconWrapperCss,
            inputCss: props.inputCss,
            regionPillWrapperCss: props.regionPillWrapperCss,
            hideArrowIcon: props.hideArrowIcon,
            onRemoveAllRegions,
            onInputMount: props.onInputMount,
            isInputWithDistanceFilter: props.includeDistanceFilter,
            showArrowOnMobile: props.showArrowOnMobile,
            onInputClick: props.onInputClick,
            isTravelTimeTabActive: props.isTravelTimeTabActive,
            inputCoreWrapperCss: props.inputCoreWrapperCss,
            dropdownIcon: props.dropdownIcon
        },
        searchProps,
        onTabTypeClick,
        search: props.search,
        searchCountry,
        activeRegionSearchTab,
        isAbroadOrHolidayTab,
        isRegionSelected,
        shouldRenderDistanceFilter: !!props.includeDistanceFilter,
        distanceFilterProps: {
            defaultValue: defaultDistanceValue,
            values: props.values ?? ({search: {label: "", tabType: null}} as SearchAutocompleteController["distanceFilterProps"]["values"]),
            onAfterChange: props.onAfterChange,
            onChange: props.onChange,
            handleDistanceSubmit: () => null,
            handleDistanceAfterChange: () => undefined,
            setDesktopTypeBlendActive: () => null,
            onFilterOpen: props.isInModal ? undefined : closeDropdown,
            isMobileDropdown: true
        },
        searchBarContainerClassName: props.searchBarContainerClassName,
        disableSearchbarExtraMargins: Boolean(props.showSelectedUnderInput || props.disableExtraMargins),
        dropdownProps: {
            isOpen: isDropdownOpen,
            isInModal: props.isInModal,
            "data-testid": NAVIGATION_TEST_IDS.SEARCH_BAR.DROPDOWN,
            isInputWithDistanceFilter: props.search.currentTabData.country === Country.POLAND && !!props.includeDistanceFilter,
            isHomepage: props.isHomepage,
            activeRegionSearchTab,
            className: props.dropdownWrapperClassName,
            minWidth: props.minWidth
        },
        dropdownListProps,
        renderDropdownList: () => props.renderDropdownList(dropdownListProps) as ReactNode,
        requestStateObj,
        selectedRegions,
        isMobile: Boolean(isMobile),
        pillDistanceValue,
        prevDistance,
        onRemoveRegionTagClick,
        showSelectedUnderInput: Boolean(
            props.showSelectedUnderInput &&
            props.values?.search &&
            "regions" in props.values.search &&
            props.values.search?.regions?.length &&
            !props.isTravelTimeTabActive
        ),
        offers,
        vendors,
        regions,
        places,
        activeItem: props.search.activeDropdownItem
    };
};
